import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { fmtMoney, FMT_INT } from '../lib/money'
import { useCurrency } from '../lib/currency-context'
import { useTranslation } from 'react-i18next'
import { CopyCell } from '../components/CopyCell'
import { useSortable } from '../hooks/useSortable'
import { SortableHeader } from '../components/SortableHeader'
import { useAuth } from '../lib/auth'
import { RecordHistory } from '../components/RecordHistory'
import { ClaimPayments } from '../components/CarrierPayments'

// ─── Detection types ─────────────────────────────────────────────────────────

type FlagReason = 'label_cost_mismatch' | 'blacklisted_charge' | 'unknown_charge'

export interface SuspiciousRow {
  carrier: string
  tracking_number: string
  customer_name: string | null
  expected_minor: number | null
  actual_minor: number | null
  delta_minor: number | null
  flag_reason: FlagReason
  charge_description: string | null
  invoice_date: string | null
  invoice_number: string | null
}

interface DetectionSettings {
  carrier: string
  label_cost_threshold_pct: number
  label_cost_min_delta_minor: number
  strict_unknown_charges: boolean
  lookback_days: number
  active: boolean
  notes: string | null
}

interface ChargeRule {
  rule_id: number
  carrier: string
  charge_name_pattern: string
  rule_type: 'blacklist' | 'whitelist'
  alert_threshold_usd_minor: number | null
  active: boolean
  notes: string | null
  created_at: string
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ClaimStatus = 'open' | 'approved' | 'carrier_paid' | 'customer_credited' | 'denied' | 'disputed'
type ClaimType   = 'lost' | 'damaged' | 'delay' | 'wrong_delivery' | 'other'

interface ClaimKPIs {
  open_count: number
  total_pending_payout_minor: number
  total_owed_to_customers_minor: number
  carrier_paid_not_credited: number
}

interface ClaimRow {
  claim_id: number
  carrier: string
  tracking_number: string
  display_tracking: string
  customer_name: string
  claim_reference: string | null
  claim_type: ClaimType
  declared_value_usd_minor: number | null
  carrier_payout_usd_minor: number
  customer_payback_usd_minor: number
  currency_code: string
  exchange_rate_to_usd: number
  carrier_paid_date: string | null
  customer_credited_date: string | null
  status: ClaimStatus
  paid_back_via: string | null
  notes: string | null
  source: 'manual' | 'carrier_credit'
  rebill_id: number | null
  rebill_status: string | null
  rebill_pushed_at: string | null
  credit_memos: string | null
  created_at: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CARRIERS = ['usps', 'dhl', 'fedex', 'ups', 'asendia', 'evri', 'epost', 'intelcom', 'uniuni']
const CLAIM_TYPES: ClaimType[] = ['lost', 'damaged', 'delay', 'wrong_delivery', 'other']

const STATUS_LABEL: Record<ClaimStatus, string> = {
  open:               'Open',
  approved:           'Approved',
  carrier_paid:       'Carrier Paid',
  customer_credited:  'Customer Credited',
  denied:             'Denied',
  disputed:           'Disputed',
}

const STATUS_PILL: Record<ClaimStatus, string> = {
  open:               'pill-honey',
  approved:           'pill-royal',
  carrier_paid:       'pill-mint',
  customer_credited:  'bg-ink-200 text-ink-600',
  denied:             'pill-flame',
  disputed:           'pill-honey',
}

/** Valid next transitions from each status */
const TRANSITIONS: Record<ClaimStatus, ClaimStatus[]> = {
  open:               ['approved', 'denied', 'disputed'],
  approved:           ['carrier_paid', 'disputed', 'denied'],
  carrier_paid:       ['customer_credited', 'disputed'],
  customer_credited:  [],
  denied:             [],
  disputed:           ['approved', 'denied'],
}

function fmtDate(d: string | null) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ─── Add Claim Modal ──────────────────────────────────────────────────────────

function AddClaimModal({ onClose, onSaved, prefill }: {
  onClose: () => void
  onSaved: () => void
  prefill?: SuspiciousRow | null
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    carrier: prefill?.carrier ?? 'usps',
    tracking_number: prefill?.tracking_number ?? '',
    customer_name: prefill?.customer_name ?? '',
    claim_type: 'lost' as ClaimType,
    claim_reference: '',
    declared_value_usd: '',
    carrier_payout_usd: prefill?.delta_minor != null
      ? (prefill.delta_minor / 100).toFixed(2) : '',
    customer_payback_usd: prefill?.delta_minor != null
      ? (prefill.delta_minor / 100).toFixed(2) : '',
    currency_code: 'USD',
    carrier_paid_date: '',
    notes: prefill?.charge_description
      ? `Charge: ${prefill.charge_description}` : '',
  })
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!form.tracking_number.trim() || !form.customer_name.trim()) {
      setError('Tracking number and customer name are required.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const toMinor = (v: string) => v ? Math.round(parseFloat(v) * 100) : 0
      const res = await fetch('/api/claims', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          carrier: form.carrier,
          tracking_number: form.tracking_number.trim(),
          customer_name: form.customer_name.trim(),
          claim_type: form.claim_type,
          claim_reference: form.claim_reference.trim() || null,
          declared_value_usd_minor: form.declared_value_usd ? toMinor(form.declared_value_usd) : null,
          carrier_payout_usd_minor: toMinor(form.carrier_payout_usd),
          customer_payback_usd_minor: toMinor(form.customer_payback_usd),
          currency_code: form.currency_code,
          carrier_paid_date: form.carrier_paid_date || null,
          notes: form.notes.trim() || null,
        }),
      })
      const json = await res.json()
      if (!res.ok) { setError(json.error ?? 'Save failed'); setSaving(false); return }
      onSaved()
    } catch (e) {
      setError((e as Error).message)
      setSaving(false)
    }
  }

  const field = (label: string, node: React.ReactNode) => (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">{label}</label>
      {node}
    </div>
  )
  const inp = (key: keyof typeof form, type = 'text', placeholder = '') => (
    <input
      type={type}
      placeholder={placeholder}
      value={form[key]}
      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
      className="px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-900 focus:outline-none focus:border-royal-400"
    />
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-y-auto max-h-[90vh]">
        <div className="px-6 py-5 border-b border-ink-200 flex items-center justify-between">
          <h2 className="text-[16px] font-semibold text-ink-900">New carrier claim</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700 text-xl leading-none">×</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {field('Carrier',
              <select value={form.carrier} onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))}
                      className="px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-900">
                {CARRIERS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
              </select>
            )}
            {field('Claim type',
              <select value={form.claim_type} onChange={e => setForm(f => ({ ...f, claim_type: e.target.value as ClaimType }))}
                      className="px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-900">
                {CLAIM_TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
              </select>
            )}
          </div>
          {field('Tracking number', inp('tracking_number', 'text', '9400…'))}
          {field('Customer name (exact)', inp('customer_name', 'text', 'YouParcel Inc.'))}
          {field('Carrier claim reference', inp('claim_reference', 'text', 'CLM-123456'))}

          <div className="grid grid-cols-3 gap-4">
            {field('Declared value ($)', inp('declared_value_usd', 'number', '0.00'))}
            {field('Carrier payout ($)', inp('carrier_payout_usd', 'number', '0.00'))}
            {field('Customer payback ($)', inp('customer_payback_usd', 'number', '0.00'))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {field('Currency',
              <select value={form.currency_code} onChange={e => setForm(f => ({ ...f, currency_code: e.target.value }))}
                      className="px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-900">
                <option>USD</option><option>GBP</option><option>EUR</option><option>CAD</option>
              </select>
            )}
            {field('Carrier paid date', inp('carrier_paid_date', 'date'))}
          </div>

          {field('Notes', (
            <textarea
              rows={2}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder={t('claims.optional_context')}
              className="px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-900 resize-none focus:outline-none focus:border-royal-400"
            />
          ))}

          {error && (
            <div className="text-[13px] text-flame-700 bg-flame-50 border border-flame-200 rounded-lg px-3 py-2">{error}</div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-ink-200 flex justify-end gap-2">
          <button onClick={onClose}
                  className="px-4 py-2 rounded-lg text-[13px] font-medium border border-ink-200 text-ink-700 hover:bg-ink-50">
            {t('common.cancel')}
          </button>
          <button onClick={save} disabled={saving}
                  className="px-4 py-2 rounded-lg text-[13px] font-medium bg-ink-900 text-white hover:bg-ink-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save claim'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Claim detail drawer ──────────────────────────────────────────────────────

function ClaimDrawer({ claim, onClose }: { claim: ClaimRow | null; onClose: () => void }) {
  if (!claim) return null

  function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
      <div className="flex items-start gap-2 py-1.5 border-b border-ink-50 last:border-0">
        <span className="text-[11px] font-medium text-ink-400 w-36 shrink-0 pt-0.5">{label}</span>
        <span className="text-[12px] text-ink-800 break-all">{value ?? <span className="text-ink-300">—</span>}</span>
      </div>
    )
  }

  const isNonUSD = claim.currency_code && claim.currency_code !== 'USD'

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="w-[480px] bg-white border-l border-ink-200 shadow-2xl flex flex-col"
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100 shrink-0">
          <div>
            <span className="text-[14px] font-semibold text-ink-900">
              {claim.carrier.toUpperCase()} claim
            </span>
            <div className="mt-1">
              <span className={`pill ${STATUS_PILL[claim.status]} text-[11px]`}>
                {STATUS_LABEL[claim.status]}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700">
            <Icon name="x" className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Identity */}
          <div>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Identity</div>
            <Row label="Customer"        value={claim.customer_name} />
            <Row label="Carrier"         value={claim.carrier.toUpperCase()} />
            <Row label="Claim type"      value={
              <span className="pill bg-ink-100 text-ink-600 text-[11px]">
                {claim.claim_type.replace('_', ' ')}
              </span>
            } />
            <Row label="Claim reference" value={claim.claim_reference} />
          </div>

          {/* Shipment */}
          <div>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Shipment</div>
            <Row label="Tracking" value={<CopyCell value={claim.display_tracking} />} />
            <Row label="Opened"   value={fmtDate(claim.created_at)} />
          </div>

          {/* Financials */}
          <div>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Financials</div>
            {claim.declared_value_usd_minor != null && (
              <Row label="Declared value"   value={fmtMoney(claim.declared_value_usd_minor, 'USD')} />
            )}
            <Row label="Carrier payout"     value={
              <span className="font-semibold text-mint-700">
                {fmtMoney(claim.carrier_payout_usd_minor, 'USD')}
                {isNonUSD && <span className="text-ink-400 text-[10px] ml-1">({claim.currency_code})</span>}
              </span>
            } />
            <Row label="Customer payback"   value={
              claim.customer_payback_usd_minor > 0
                ? <span className="font-semibold text-flame-700">{fmtMoney(claim.customer_payback_usd_minor, 'USD')}</span>
                : null
            } />
            {isNonUSD && (
              <Row label="Exchange rate" value={`1 ${claim.currency_code} = ${Number(claim.exchange_rate_to_usd).toFixed(4)} USD`} />
            )}
          </div>

          {/* Lifecycle */}
          <div>
            <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Lifecycle</div>
            <Row label="Carrier paid"       value={fmtDate(claim.carrier_paid_date)} />
            <Row label="Customer credited"  value={fmtDate(claim.customer_credited_date)} />
            <Row label="Paid back via"      value={claim.paid_back_via} />
          </div>

          {/* Notes */}
          {claim.notes && (
            <div>
              <div className="text-[10px] font-semibold text-ink-400 uppercase tracking-wide mb-2">Notes</div>
              <p className="text-[11px] text-ink-700 whitespace-pre-wrap bg-ink-50 rounded-md p-3">{claim.notes}</p>
            </div>
          )}

          <ClaimPayments claimId={claim.claim_id} />

          <RecordHistory entity="carrier_claims" id={claim.claim_id} />
        </div>
      </div>
    </div>
  )
}

// ─── To review (was "Suspicious") ─────────────────────────────────────────────
// Overcharges that nothing else has caught: the API leaves out parcels that
// already have a rebill or a dispute. Shown on Carrier Recovery → To review.

const FLAG_LABEL: Record<FlagReason, string> = {
  label_cost_mismatch: 'Cost mismatch',
  blacklisted_charge:  'Blacklisted charge',
  unknown_charge:      'Unknown charge',
}
const FLAG_PILL: Record<FlagReason, string> = {
  label_cost_mismatch: 'pill-flame',
  blacklisted_charge:  'pill-honey',
  unknown_charge:      'bg-ink-200 text-ink-600',
}

export function ToReviewTab({ onDispute, canDispute }: {
  onDispute: (row: SuspiciousRow) => void
  canDispute: boolean
}) {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const qc = useQueryClient()
  const [carrierFilter, setCarrierFilter] = useState('')
  const [dismissTarget, setDismissTarget] = useState<SuspiciousRow | null>(null)
  const [dismissReason, setDismissReason] = useState('')

  const params = new URLSearchParams()
  if (carrierFilter) params.set('carrier', carrierFilter)

  const suspicious = useQuery<{ suspicious: SuspiciousRow[]; count: number }>({
    queryKey: ['detection', 'suspicious', carrierFilter],
    queryFn: () => fetch(`/api/detection/suspicious?${params}`).then(r => r.json()),
  })

  const dismissMutation = useMutation({
    mutationFn: (row: SuspiciousRow & { dismiss_reason?: string }) =>
      fetch('/api/detection/dismiss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          carrier: row.carrier,
          tracking_number: row.tracking_number,
          flag_reason: row.flag_reason,
          charge_description: row.charge_description,
          dismiss_reason: row.dismiss_reason,
        }),
      }).then(r => r.json()),
    onSuccess: () => {
      setDismissTarget(null)
      setDismissReason('')
      qc.invalidateQueries({ queryKey: ['detection', 'suspicious'] })
    },
  })

  const rows = suspicious.data?.suspicious ?? []
  const { sort, onSort, sorted } = useSortable(rows, 'invoice_date', 'desc')

  return (
    <div className="space-y-4">
      {/* Dismiss confirm dialog */}
      {dismissTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="text-[15px] font-semibold text-ink-900 mb-1">Ignore this overcharge?</div>
            <p className="text-[12px] text-ink-500 mb-2">It will not be listed again.</p>
            <p className="text-[12px] text-ink-500 mb-3">
              {dismissTarget.carrier.toUpperCase()} · {dismissTarget.tracking_number}<br />
              {FLAG_LABEL[dismissTarget.flag_reason]}
              {dismissTarget.charge_description && ` · "${dismissTarget.charge_description}"`}
            </p>
            <input
              type="text"
              placeholder={t('claims.reason_optional')}
              value={dismissReason}
              onChange={e => setDismissReason(e.target.value)}
              className="w-full px-3 py-2 border border-ink-200 rounded-lg text-[13px] mb-4 focus:outline-none focus:border-royal-400"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setDismissTarget(null); setDismissReason('') }}
                      className="px-4 py-2 rounded-lg text-[13px] font-medium border border-ink-200 text-ink-700 hover:bg-ink-50">
                {t('common.cancel')}
              </button>
              <button
                disabled={dismissMutation.isPending}
                onClick={() => dismissMutation.mutate({ ...dismissTarget, dismiss_reason: dismissReason })}
                className="px-4 py-2 rounded-lg text-[13px] font-medium bg-ink-900 text-white hover:bg-ink-700 disabled:opacity-50"
              >
                {dismissMutation.isPending ? 'Saving…' : 'Ignore'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <select value={carrierFilter} onChange={e => setCarrierFilter(e.target.value)}
                className="px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700">
<option value="">{t('claims.all_carriers')}</option>
          {CARRIERS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
        </select>
        <button onClick={() => qc.invalidateQueries({ queryKey: ['detection', 'suspicious'] })}
                className="px-3 py-2 border border-ink-200 rounded-lg text-[13px] text-ink-700 hover:bg-ink-50">
          ↻ Refresh
        </button>
        {rows.length > 0 && (
          <span className="text-[13px] text-ink-500">{rows.length} to review</span>
        )}
      </div>
      <p className="text-[12.5px] text-ink-500 -mt-1">
        The carrier billed clearly more than the label cost, and no rebill or dispute covers it yet.
        Dispute it, or ignore it if the difference is fine (for example fuel or a residential charge).
      </p>

      <Card className="p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                <SortableHeader col="carrier"         label="Carrier"       sort={sort} onSort={onSort} className="pr-3" />
                <SortableHeader col="tracking_number" label="Tracking"      sort={sort} onSort={onSort} className="pr-3" />
                <SortableHeader col="customer_name"   label="Customer"      sort={sort} onSort={onSort} className="pr-3" />
                <SortableHeader col="flag_reason"     label="Flag"          sort={sort} onSort={onSort} className="pr-3" />
                <th className="text-left py-2 pr-3">Charge</th>
                <th className="text-right py-2 pr-3">Expected</th>
                <th className="text-right py-2 pr-3">Billed</th>
                <SortableHeader col="delta_minor"     label="Delta"         sort={sort} onSort={onSort} className="pr-3" numeric />
                <SortableHeader col="invoice_date"    label="Invoice date"  sort={sort} onSort={onSort} className="pr-3" />
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => (
                <tr key={`${r.carrier}-${r.tracking_number}-${r.flag_reason}-${i}`}
                    className="border-b border-ink-100 hover:bg-ink-50">
                  <td className="py-2 pr-3 font-medium uppercase text-[12px]">{r.carrier}</td>
                  <td className="py-2 pr-3"><CopyCell value={r.tracking_number} /></td>
                  <td className="py-2 pr-3">{r.customer_name ?? <span className="text-ink-400">—</span>}</td>
                  <td className="py-2 pr-3">
                    <span className={`pill ${FLAG_PILL[r.flag_reason]} text-[11px]`}>
                      {FLAG_LABEL[r.flag_reason]}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-ink-600 text-[12px] max-w-[140px] truncate"
                      title={r.charge_description ?? ''}>
                    {r.charge_description ?? <span className="text-ink-300">—</span>}
                  </td>
                  <td className="py-2 pr-3 text-right num text-ink-500">
                    {r.expected_minor != null ? fmtMoney(r.expected_minor, currency) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right num font-semibold">
                    {r.actual_minor != null ? fmtMoney(r.actual_minor, currency) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right num font-semibold text-flame-700">
                    {r.delta_minor != null ? `+${fmtMoney(r.delta_minor, currency)}` : '—'}
                  </td>
                  <td className="py-2 pr-3 text-ink-500 text-[12px]">{fmtDate(r.invoice_date)}</td>
                  <td className="py-2">
                    <div className="flex gap-1">
                      {canDispute && (
                        <button
                          onClick={() => onDispute(r)}
                          className="px-2 py-1 rounded text-[11px] font-medium bg-ink-900 text-white hover:bg-ink-700"
                        >
                          Dispute…
                        </button>
                      )}
                      <button
                        onClick={() => setDismissTarget(r)}
                        className="px-2 py-1 rounded text-[11px] font-medium border border-ink-200 text-ink-600 hover:bg-ink-50"
                      >
                        Ignore
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {suspicious.isLoading && (
                <tr><td colSpan={10} className="py-8 text-center text-ink-400">Running detection…</td></tr>
              )}
              {!suspicious.isLoading && rows.length === 0 && (
                <tr><td colSpan={10} className="py-8 text-center text-ink-400">
                  Nothing to review: every overcharge has a rebill or a dispute.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

// ─── Rules tab ────────────────────────────────────────────────────────────────

export function RulesTab() {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const qc = useQueryClient()
  const [showAddRule, setShowAddRule] = useState(false)
  const [editSettings, setEditSettings] = useState<string | null>(null) // carrier being edited
  const [settingsForm, setSettingsForm] = useState<Partial<DetectionSettings>>({})
  const [newRule, setNewRule] = useState({
    carrier: 'fedex', charge_name_pattern: '', rule_type: 'blacklist' as 'blacklist' | 'whitelist',
    alert_threshold_usd: '', notes: '',
  })

  const rules = useQuery<{ rules: ChargeRule[] }>({
    queryKey: ['detection', 'rules'],
    queryFn: () => fetch('/api/detection/rules').then(r => r.json()),
  })
  const settings = useQuery<{ settings: DetectionSettings[] }>({
    queryKey: ['detection', 'settings'],
    queryFn: () => fetch('/api/detection/settings').then(r => r.json()),
  })

  const addRuleMutation = useMutation({
    mutationFn: () => fetch('/api/detection/rules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        carrier: newRule.carrier,
        charge_name_pattern: newRule.charge_name_pattern,
        rule_type: newRule.rule_type,
        alert_threshold_usd_minor: newRule.alert_threshold_usd
          ? Math.round(parseFloat(newRule.alert_threshold_usd) * 100) : null,
        notes: newRule.notes || null,
      }),
    }).then(r => r.json()),
    onSuccess: () => {
      setShowAddRule(false)
      setNewRule({ carrier: 'fedex', charge_name_pattern: '', rule_type: 'blacklist', alert_threshold_usd: '', notes: '' })
      qc.invalidateQueries({ queryKey: ['detection', 'rules'] })
    },
  })

  const toggleRuleMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      fetch(`/api/detection/rules/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active }),
      }).then(r => r.json()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['detection', 'rules'] }),
  })

  const saveSettingsMutation = useMutation({
    mutationFn: ({ carrier, data }: { carrier: string; data: Partial<DetectionSettings> }) =>
      fetch(`/api/detection/settings/${carrier}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }).then(r => r.json()),
    onSuccess: () => {
      setEditSettings(null)
      qc.invalidateQueries({ queryKey: ['detection', 'settings'] })
    },
  })

  const ruleRows = rules.data?.rules ?? []
  const settingRows = settings.data?.settings ?? []

  return (
    <div className="space-y-6">
      {/* ── Charge rules ── */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[14px] font-semibold text-ink-900">Charge rules</div>
            <div className="text-[12px] text-ink-500 mt-0.5">
              Blacklist = always flag. Whitelist = never flag. Patterns use SQL ILIKE (% wildcard).
            </div>
          </div>
          <button onClick={() => setShowAddRule(true)}
                  className="px-3 py-2 rounded-lg text-[13px] font-semibold bg-ink-900 text-white hover:bg-ink-700">
            + Add rule
          </button>
        </div>

        {showAddRule && (
          <div className="border border-ink-200 rounded-lg p-4 mb-4 bg-ink-50 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Carrier</label>
                <select value={newRule.carrier} onChange={e => setNewRule(f => ({ ...f, carrier: e.target.value }))}
                        className="mt-1 w-full px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white">
                  {CARRIERS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Rule type</label>
                <select value={newRule.rule_type} onChange={e => setNewRule(f => ({ ...f, rule_type: e.target.value as 'blacklist' | 'whitelist' }))}
                        className="mt-1 w-full px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white">
                  <option value="blacklist">🔴 Blacklist (always flag)</option>
                  <option value="whitelist">🟢 Whitelist (never flag)</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
                Charge name pattern
              </label>
              <input type="text"
                     value={newRule.charge_name_pattern}
                     onChange={e => setNewRule(f => ({ ...f, charge_name_pattern: e.target.value }))}
                     placeholder={t('claims.charge_pattern_placeholder')}
                     className="mt-1 w-full px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white focus:outline-none focus:border-royal-400" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">
                  Min amount to alert ($) — optional
                </label>
                <input type="number" placeholder="0.00"
                       value={newRule.alert_threshold_usd}
                       onChange={e => setNewRule(f => ({ ...f, alert_threshold_usd: e.target.value }))}
                       className="mt-1 w-full px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white focus:outline-none focus:border-royal-400" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Notes</label>
                <input type="text"
                       value={newRule.notes}
                       onChange={e => setNewRule(f => ({ ...f, notes: e.target.value }))}
                       className="mt-1 w-full px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white focus:outline-none focus:border-royal-400" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowAddRule(false)}
                      className="px-3 py-1.5 rounded-lg text-[13px] border border-ink-200 text-ink-700 hover:bg-white">Cancel</button>
              <button onClick={() => addRuleMutation.mutate()}
                      disabled={!newRule.charge_name_pattern || addRuleMutation.isPending}
                      className="px-3 py-1.5 rounded-lg text-[13px] font-medium bg-ink-900 text-white hover:bg-ink-700 disabled:opacity-50">
                {addRuleMutation.isPending ? 'Saving…' : 'Save rule'}
              </button>
            </div>
          </div>
        )}

        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
              <th className="text-left py-2 pr-3">Carrier</th>
              <th className="text-left py-2 pr-3">Pattern</th>
              <th className="text-left py-2 pr-3">Type</th>
              <th className="text-right py-2 pr-3">Min alert</th>
              <th className="text-left py-2 pr-3">Notes</th>
              <th className="text-center py-2 pr-3">Active</th>
            </tr>
          </thead>
          <tbody>
            {ruleRows.map(r => (
              <tr key={r.rule_id} className={`border-b border-ink-100 hover:bg-ink-50 ${!r.active ? 'opacity-40' : ''}`}>
                <td className="py-2 pr-3 uppercase font-medium text-[12px]">{r.carrier}</td>
                <td className="py-2 pr-3 font-mono text-[12px]">{r.charge_name_pattern}</td>
                <td className="py-2 pr-3">
                  <span className={`pill text-[11px] ${r.rule_type === 'blacklist' ? 'pill-flame' : 'pill-mint'}`}>
                    {r.rule_type === 'blacklist' ? '🔴 Blacklist' : '🟢 Whitelist'}
                  </span>
                </td>
                <td className="py-2 pr-3 text-right num text-ink-500">
                  {r.alert_threshold_usd_minor != null
                    ? fmtMoney(r.alert_threshold_usd_minor, currency)
                    : <span className="text-ink-300">any</span>}
                </td>
                <td className="py-2 pr-3 text-ink-500 text-[12px]">{r.notes ?? '—'}</td>
                <td className="py-2 pr-3 text-center">
                  <button
                    onClick={() => toggleRuleMutation.mutate({ id: r.rule_id, active: !r.active })}
                    disabled={toggleRuleMutation.isPending}
                    className={`w-8 h-4 rounded-full transition-colors ${r.active ? 'bg-mint-500' : 'bg-ink-300'}`}
                    title={r.active ? 'Disable rule' : 'Enable rule'}
                  />
                </td>
              </tr>
            ))}
            {rules.isLoading && <tr><td colSpan={6} className="py-6 text-center text-ink-400">Loading…</td></tr>}
            {!rules.isLoading && ruleRows.length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-ink-400">No rules yet — add one above</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* ── Per-carrier detection settings ── */}
      <Card className="p-5">
        <div className="text-[14px] font-semibold text-ink-900 mb-1">Detection thresholds</div>
        <div className="text-[12px] text-ink-500 mb-4">
          Flag if carrier billed &gt; label cost × (1 + threshold%). Min delta ignores rounding noise.
        </div>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
              <th className="text-left py-2 pr-3">Carrier</th>
              <th className="text-right py-2 pr-3">Threshold %</th>
              <th className="text-right py-2 pr-3">Min delta</th>
              <th className="text-right py-2 pr-3">Lookback days</th>
              <th className="text-center py-2 pr-3">Active</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {settingRows.map(s => {
              const isEditing = editSettings === s.carrier
              return (
                <tr key={s.carrier} className="border-b border-ink-100 hover:bg-ink-50">
                  <td className="py-2 pr-3 uppercase font-medium text-[12px]">{s.carrier}</td>
                  <td className="py-2 pr-3 text-right num">
                    {isEditing
                      ? <input type="number" step="0.5"
                               defaultValue={s.label_cost_threshold_pct}
                               onChange={e => setSettingsForm(f => ({ ...f, label_cost_threshold_pct: parseFloat(e.target.value) }))}
                               className="w-20 px-2 py-1 border border-royal-400 rounded text-[12px] text-right" />
                      : `${s.label_cost_threshold_pct}%`}
                  </td>
                  <td className="py-2 pr-3 text-right num">
                    {isEditing
                      ? <input type="number"
                               defaultValue={(s.label_cost_min_delta_minor / 100).toFixed(2)}
                               onChange={e => setSettingsForm(f => ({ ...f, label_cost_min_delta_minor: Math.round(parseFloat(e.target.value) * 100) }))}
                               className="w-20 px-2 py-1 border border-royal-400 rounded text-[12px] text-right" />
                      : fmtMoney(s.label_cost_min_delta_minor, 'USD')}
                  </td>
                  <td className="py-2 pr-3 text-right num">
                    {isEditing
                      ? <input type="number"
                               defaultValue={s.lookback_days}
                               onChange={e => setSettingsForm(f => ({ ...f, lookback_days: parseInt(e.target.value) }))}
                               className="w-20 px-2 py-1 border border-royal-400 rounded text-[12px] text-right" />
                      : `${s.lookback_days}d`}
                  </td>
                  <td className="py-2 pr-3 text-center">
                    <span className={`w-2 h-2 rounded-full inline-block ${s.active ? 'bg-mint-500' : 'bg-ink-300'}`} />
                  </td>
                  <td className="py-2 text-right">
                    {isEditing ? (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => { setEditSettings(null); setSettingsForm({}) }}
                                className="px-2 py-1 text-[11px] border border-ink-200 rounded hover:bg-ink-50">Cancel</button>
                        <button
                          onClick={() => saveSettingsMutation.mutate({ carrier: s.carrier, data: settingsForm })}
                          disabled={saveSettingsMutation.isPending}
                          className="px-2 py-1 text-[11px] bg-ink-900 text-white rounded hover:bg-ink-700 disabled:opacity-50">
                          {saveSettingsMutation.isPending ? '…' : 'Save'}
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditSettings(s.carrier); setSettingsForm({}) }}
                              className="text-[12px] text-ink-400 hover:text-ink-700">Edit</button>
                    )}
                  </td>
                </tr>
              )
            })}
            {settings.isLoading && <tr><td colSpan={6} className="py-6 text-center text-ink-400">Loading…</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

// ─── Carrier credits tab ──────────────────────────────────────────────────────
// Claims made from carrier credit memos (ePost, Asendia claims). Support picks
// one or many and credits the customer: each becomes a negative rebill
// (0% markup) that goes to ByeLabel with the normal Push on Carrier Adjustments.

type CreditFilter = 'to_credit' | 'credited' | 'all'

function isCreditable(r: ClaimRow) {
  return r.status === 'carrier_paid' && r.rebill_id === null && r.currency_code === 'USD'
}

function CreditAmountInput({ claim, onSaved }: { claim: ClaimRow; onSaved: () => void }) {
  const [value, setValue] = useState((claim.customer_payback_usd_minor / 100).toFixed(2))
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function save() {
    const minor = Math.round(Number(value) * 100)
    if (minor === claim.customer_payback_usd_minor) { setError(null); return }
    if (!Number.isFinite(minor) || minor <= 0 || minor > claim.carrier_payout_usd_minor) {
      setError(`0.01 – ${(claim.carrier_payout_usd_minor / 100).toFixed(2)}`)
      return
    }
    setSaving(true)
    try {
      const res = await fetch(`/api/claims/${claim.claim_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_payback_usd_minor: minor }),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) { setError(j.error ?? `HTTP ${res.status}`); return }
      setError(null)
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center gap-1">
        <span className="text-ink-400">$</span>
        <input value={value} disabled={saving}
          onChange={e => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
          className="w-20 h-7 px-2 text-right border border-ink-200 rounded-md num text-[12.5px]" />
      </div>
      {error && <span className="text-[10.5px] text-flame-700">{error}</span>}
    </div>
  )
}

function CarrierCreditsTab({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<CreditFilter>('to_credit')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [confirming, setConfirming] = useState(false)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{ ok: boolean; text: string; problems?: string[] } | null>(null)

  const list = useQuery<{ claims: ClaimRow[] }>({
    queryKey: ['claims', 'credits'],
    queryFn: () => fetch('/api/claims?source=carrier_credit&limit=500').then(r => r.json()),
  })
  const all = list.data?.claims ?? []
  const rows = all.filter(r =>
    filter === 'all' ? true : filter === 'credited' ? r.rebill_id !== null : isCreditable(r))
  const { sort, onSort, sorted } = useSortable(rows, 'carrier_paid_date', 'desc')

  const chosen = all.filter(r => selected.has(r.claim_id) && isCreditable(r))
  const chosenTotal = chosen.reduce((s, r) => s + r.customer_payback_usd_minor, 0)
  const creditable = rows.filter(isCreditable)
  const allChosen = creditable.length > 0 && creditable.every(r => selected.has(r.claim_id))

  const refresh = () => qc.invalidateQueries({ queryKey: ['claims'] })

  function toggle(id: number) {
    setConfirming(false)
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  async function send() {
    setSending(true)
    setResult(null)
    try {
      const res = await fetch('/api/claims/credit-to-customer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ claim_ids: chosen.map(r => r.claim_id), confirm: true }),
      })
      const j = await res.json().catch(() => ({}))
      if (!res.ok) {
        setResult({ ok: false, text: j.error ?? `HTTP ${res.status}`, problems: j.problems })
      } else {
        const n = (j.credited ?? []).length
        setResult({ ok: true, text: `${n} claim credit${n === 1 ? '' : 's'} created as rebills. Push them from Carrier Adjustments.` })
        setSelected(new Set())
      }
    } finally {
      setSending(false)
      setConfirming(false)
      refresh()
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        {([['to_credit', 'To credit'], ['credited', 'Credited'], ['all', 'All']] as const).map(([v, label]) => (
          <button key={v} onClick={() => { setFilter(v); setConfirming(false) }}
            className={`h-8 px-3 rounded-lg text-[12.5px] font-semibold border ${
              filter === v ? 'bg-ink-900 text-white border-ink-900' : 'border-ink-200 text-ink-600 hover:bg-ink-50'}`}>
            {label}
          </button>
        ))}
        <div className="flex-1" />
        {canManage && chosen.length > 0 && !confirming && (
          <button onClick={() => setConfirming(true)}
            className="h-8 px-3 rounded-lg bg-flame-500 hover:bg-flame-600 text-white font-semibold text-[12.5px]">
            Credit {chosen.length} to customer{chosen.length === 1 ? '' : 's'} · {fmtMoney(chosenTotal, 'USD')}
          </button>
        )}
        {canManage && confirming && (
          <div className="flex items-center gap-2">
            <span className="text-[12.5px] text-ink-600">
              Create {chosen.length} credit rebill{chosen.length === 1 ? '' : 's'} ({fmtMoney(chosenTotal, 'USD')})? Each claim can be credited only once.
            </span>
            <button onClick={() => setConfirming(false)} disabled={sending}
              className="h-8 px-3 rounded-lg border border-ink-200 text-ink-700 text-[12.5px] hover:bg-ink-50">
              Cancel
            </button>
            <button onClick={send} disabled={sending}
              className="h-8 px-3 rounded-lg bg-flame-600 hover:bg-flame-700 text-white font-semibold text-[12.5px] disabled:opacity-50">
              {sending ? 'Crediting…' : 'Yes, credit'}
            </button>
          </div>
        )}
      </div>

      {!canManage && (
        <div className="text-[12px] text-ink-500">You need the "Claims" action permission to credit customers.</div>
      )}
      {result && (
        <div className={`text-[12.5px] rounded-lg px-3 py-2 ${result.ok ? 'bg-mint-50 text-mint-800' : 'bg-flame-50 text-flame-800'}`}>
          {result.text}
          {result.problems && <ul className="list-disc ml-5 mt-1">{result.problems.map(p => <li key={p}>{p}</li>)}</ul>}
        </div>
      )}

      <Card className="p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                <th className="py-2 pr-2 w-6">
                  {canManage && creditable.length > 0 && (
                    <input type="checkbox" checked={allChosen}
                      onChange={() => { setConfirming(false); setSelected(allChosen ? new Set() : new Set(creditable.map(r => r.claim_id))) }} />
                  )}
                </th>
                <SortableHeader col="carrier"                    label="Carrier"         sort={sort} onSort={onSort} className="pr-3" />
                <SortableHeader col="tracking_number"            label="Tracking"        sort={sort} onSort={onSort} className="pr-3" />
                <SortableHeader col="customer_name"              label="Customer"        sort={sort} onSort={onSort} className="pr-3" />
                <th className="text-left py-2 pr-3">Type</th>
                <th className="text-left py-2 pr-3">Credit memo</th>
                <SortableHeader col="carrier_paid_date"          label="Carrier paid"    sort={sort} onSort={onSort} className="pr-3" />
                <SortableHeader col="carrier_payout_usd_minor"   label="Carrier credit"  sort={sort} onSort={onSort} className="pr-3" numeric />
                <SortableHeader col="customer_payback_usd_minor" label="Customer credit" sort={sort} onSort={onSort} className="pr-3" numeric />
                <th className="text-left py-2">Rebill</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => (
                <tr key={r.claim_id} className="border-b border-ink-100 hover:bg-ink-50">
                  <td className="py-2 pr-2">
                    {canManage && isCreditable(r) && (
                      <input type="checkbox" checked={selected.has(r.claim_id)} onChange={() => toggle(r.claim_id)} />
                    )}
                  </td>
                  <td className="py-2 pr-3 font-medium uppercase text-[12px]">{r.carrier}</td>
                  <td className="py-2 pr-3"><CopyCell value={r.display_tracking} /></td>
                  <td className="py-2 pr-3 font-medium">{r.customer_name}</td>
                  <td className="py-2 pr-3">
                    <span className="pill bg-ink-100 text-ink-600 text-[11px]">{r.claim_type.replace('_', ' ')}</span>
                  </td>
                  <td className="py-2 pr-3 text-ink-500 text-[12px]">{r.credit_memos ?? r.claim_reference ?? '—'}</td>
                  <td className="py-2 pr-3 text-ink-500">{fmtDate(r.carrier_paid_date)}</td>
                  <td className="py-2 pr-3 text-right num">{fmtMoney(r.carrier_payout_usd_minor, 'USD')}</td>
                  <td className="py-2 pr-3 text-right num">
                    {canManage && isCreditable(r)
                      ? <CreditAmountInput key={`${r.claim_id}-${r.customer_payback_usd_minor}`} claim={r} onSaved={refresh} />
                      : <span className="font-semibold">{fmtMoney(r.customer_payback_usd_minor, 'USD')}</span>}
                  </td>
                  <td className="py-2 text-[12px]">
                    {r.rebill_id !== null
                      ? <span className="text-ink-600">#{r.rebill_id} · {r.rebill_pushed_at ? 'pushed' : (r.rebill_status ?? '—').toLowerCase()}</span>
                      : <span className={`pill ${STATUS_PILL[r.status]} text-[11px]`}>{STATUS_LABEL[r.status]}</span>}
                  </td>
                </tr>
              ))}
              {list.isLoading && (
                <tr><td colSpan={10} className="py-8 text-center text-ink-400">Loading…</td></tr>
              )}
              {!list.isLoading && rows.length === 0 && (
                <tr><td colSpan={10} className="py-8 text-center text-ink-400">No carrier credits here</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

// ─── Main view ────────────────────────────────────────────────────────────────

type MainTab = 'claims' | 'credits'

// embedded: shown as the Claims tab of Carrier Recovery (no page title)
export function ClaimsView({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation()
  const { currency } = useCurrency()
  const qc = useQueryClient()
  const { can } = useAuth()
  const canManage = can('manage_claims')
  const [mainTab, setMainTab] = useState<MainTab>('claims')
  const [statusFilter, setStatusFilter] = useState('')
  const [carrierFilter, setCarrierFilter] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [detailClaim, setDetailClaim] = useState<ClaimRow | null>(null)
  // Pre-fill add-claim form from a suspicious row
  const [prefillFromSuspicious, setPrefillFromSuspicious] = useState<SuspiciousRow | null>(null)
  const [openMenu, setOpenMenu] = useState<number | null>(null)
  const [confirmTransition, setConfirmTransition] = useState<{
    claimId: number; toStatus: ClaimStatus; label: string
  } | null>(null)

  // Called from SuspiciousTab when "Open claim" is clicked
  const handleOpenClaimFromSuspicious = (row: SuspiciousRow) => {
    setPrefillFromSuspicious(row)
    setMainTab('claims')
    setShowAdd(true)
  }

  const kpis = useQuery<ClaimKPIs>({
    queryKey: ['claims', 'kpis'],
    queryFn: () => fetch('/api/claims/kpis').then(r => r.json()),
  })

  const params = new URLSearchParams()
  if (statusFilter)  params.set('status', statusFilter)
  if (carrierFilter) params.set('carrier', carrierFilter)

  const claims = useQuery<{ claims: ClaimRow[] }>({
    queryKey: ['claims', 'list', statusFilter, carrierFilter],
    queryFn: () => fetch(`/api/claims?${params}`).then(r => r.json()),
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ClaimStatus }) =>
      fetch(`/api/claims/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      }).then(r => r.json()),
    onSuccess: () => {
      setOpenMenu(null)
      setConfirmTransition(null)
      qc.invalidateQueries({ queryKey: ['claims'] })
    },
  })

  const k = kpis.data
  const rows = claims.data?.claims ?? []
  const { sort, onSort, sorted } = useSortable(rows, 'created_at', 'desc')

  return (
    <div className="space-y-5">
      <ClaimDrawer claim={detailClaim} onClose={() => setDetailClaim(null)} />

      {showAdd && (
        <AddClaimModal
          prefill={prefillFromSuspicious}
          onClose={() => { setShowAdd(false); setPrefillFromSuspicious(null) }}
          onSaved={() => {
            setShowAdd(false)
            setPrefillFromSuspicious(null)
            qc.invalidateQueries({ queryKey: ['claims'] })
          }}
        />
      )}

      {/* Confirm transition dialog */}
      {confirmTransition && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="text-[15px] font-semibold text-ink-900 mb-2">
              Move to "{STATUS_LABEL[confirmTransition.toStatus]}"?
            </div>
            <p className="text-[13px] text-ink-500 mb-5">
              This will update the claim status. You can still change it again later unless it's a terminal state.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmTransition(null)}
                      className="px-4 py-2 rounded-lg text-[13px] font-medium border border-ink-200 text-ink-700 hover:bg-ink-50">
                {t('common.cancel')}
              </button>
              <button
                disabled={statusMutation.isPending}
                onClick={() => statusMutation.mutate({ id: confirmTransition.claimId, status: confirmTransition.toStatus })}
                className="px-4 py-2 rounded-lg text-[13px] font-medium bg-ink-900 text-white hover:bg-ink-700 disabled:opacity-50"
              >
                {statusMutation.isPending ? 'Updating…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        {embedded ? (
          <p className="text-[13px] text-ink-500">Lost or damaged parcels: the carrier pays us back, and we credit the customer.</p>
        ) : (
          <div>
            <h1 className="text-[24px] font-bold text-ink-900">Carrier Claims</h1>
            <p className="text-[13px] text-ink-500 mt-0.5">
              Lost, damaged, and delayed shipment claims from carriers
            </p>
          </div>
        )}
        {mainTab === 'claims' && canManage && (
          <button
            onClick={() => { setPrefillFromSuspicious(null); setShowAdd(true) }}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold bg-ink-900 text-white hover:bg-ink-700"
          >
            + New claim
          </button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-ink-200">
        {/* The old Suspicious and Rules tabs moved to Carrier Recovery ("To review", "Settings"). */}
        {([
          { key: 'claims',    label: 'Claims list' },
          { key: 'credits',   label: 'Carrier credits' },
        ] as { key: MainTab; label: string }[]).map(t => (
          <button
            key={t.key}
            onClick={() => setMainTab(t.key)}
            className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              mainTab === t.key
                ? 'border-ink-900 text-ink-900'
                : 'border-transparent text-ink-500 hover:text-ink-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Carrier credits tab */}
      {mainTab === 'credits' && <CarrierCreditsTab canManage={canManage} />}

      {/* Claims tab content below */}
      {mainTab !== 'claims' ? null : <>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Open claims</div>
          <div className="mt-2 text-[28px] font-bold text-ink-900">
            {k ? FMT_INT.format(k.open_count) : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">not yet closed</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Pending payout</div>
          <div className="mt-2 text-[28px] font-bold text-ink-900">
            {k ? fmtMoney(k.total_pending_payout_minor, currency) : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">approved/carrier-paid claims</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Owed to customers</div>
          <div className="mt-2 text-[28px] font-bold text-flame-700">
            {k ? fmtMoney(k.total_owed_to_customers_minor, currency) : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">customer payback pending</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Carrier paid · uncredited</div>
          <div className="mt-2 text-[28px] font-bold text-honey-700">
            {k ? FMT_INT.format(k.carrier_paid_not_credited) : '—'}
          </div>
          <div className="text-[11px] text-ink-500 mt-1">carrier paid, customer not yet credited</div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 items-center">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
        >
<option value="">{t('claims.all_statuses')}</option>
          {Object.entries(STATUS_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          value={carrierFilter}
          onChange={e => setCarrierFilter(e.target.value)}
          className="px-3 py-2 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
        >
<option value="">{t('claims.all_carriers')}</option>
          {CARRIERS.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
        </select>
        {(statusFilter || carrierFilter) && (
          <button
            onClick={() => { setStatusFilter(''); setCarrierFilter('') }}
            className="text-[13px] text-ink-500 hover:text-ink-900 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <Card className="p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-ink-500 border-b border-ink-200">
                <SortableHeader col="carrier"                  label="Carrier"          sort={sort} onSort={onSort} className="pr-3" />
                <SortableHeader col="tracking_number"          label="Tracking"         sort={sort} onSort={onSort} className="pr-3" />
                <SortableHeader col="customer_name"            label="Customer"         sort={sort} onSort={onSort} className="pr-3" />
                <th className="text-left py-2 pr-3">Type</th>
                <SortableHeader col="carrier_payout_usd_minor" label="Carrier payout"  sort={sort} onSort={onSort} className="pr-3" numeric />
                <th className="text-right py-2 pr-3">Owed to customer</th>
                <SortableHeader col="carrier_paid_date"        label="Carrier paid"     sort={sort} onSort={onSort} className="pr-3" />
                <SortableHeader col="status"                   label="Status"           sort={sort} onSort={onSort} className="pr-3" />
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {sorted.map(r => {
                // carrier-credit claims are credited from the Carrier credits tab only
                const transitions = TRANSITIONS[r.status].filter(s =>
                  !(r.source === 'carrier_credit' && s === 'customer_credited'))
                const isMenuOpen = openMenu === r.claim_id
                return (
                  <tr key={r.claim_id}
                      className="border-b border-ink-100 hover:bg-ink-50 cursor-pointer"
                      onClick={() => setDetailClaim(r)}>
                    <td className="py-2 pr-3 font-medium uppercase text-[12px]">{r.carrier}</td>
                    <td className="py-2 pr-3"><CopyCell value={r.tracking_number} /></td>
                    <td className="py-2 pr-3 font-medium">{r.customer_name}</td>
                    <td className="py-2 pr-3">
                      <span className="pill bg-ink-100 text-ink-600 text-[11px]">
                        {r.claim_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right num font-semibold">
                      {fmtMoney(r.carrier_payout_usd_minor, currency)}
                      {r.currency_code !== 'USD' && (
                        <span className="text-ink-400 text-[10px] ml-1">{r.currency_code}</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-right num">
                      {r.customer_payback_usd_minor > 0
                        ? <span className="font-semibold text-flame-700">{fmtMoney(r.customer_payback_usd_minor, currency)}</span>
                        : <span className="text-ink-300">—</span>
                      }
                    </td>
                    <td className="py-2 pr-3 text-ink-500">{fmtDate(r.carrier_paid_date)}</td>
                    <td className="py-2 pr-3">
                      <span className={`pill ${STATUS_PILL[r.status]} text-[11px]`}>
                        {STATUS_LABEL[r.status]}
                      </span>
                    </td>
                    <td className="py-2 relative">
                      {canManage && r.rebill_id === null && transitions.length > 0 && (
                        <>
                          <button
                            onClick={e => { e.stopPropagation(); setOpenMenu(isMenuOpen ? null : r.claim_id) }}
                            className="text-ink-400 hover:text-ink-700 px-1 rounded"
                            title="Update status"
                          >
                            ⋯
                          </button>
                          {isMenuOpen && (
                            <div
                              className="absolute right-0 top-7 z-10 bg-white border border-ink-200 rounded-lg shadow-lg min-w-[180px] py-1"
                              onMouseLeave={() => setOpenMenu(null)}
                            >
                              <div className="px-3 py-1.5 text-[11px] font-semibold text-ink-400 uppercase tracking-wider">
                                Move to
                              </div>
                              {transitions.map(s => (
                                <button
                                  key={s}
                                  onClick={() => {
                                    setOpenMenu(null)
                                    setConfirmTransition({ claimId: r.claim_id, toStatus: s, label: STATUS_LABEL[s] })
                                  }}
                                  className="w-full text-left px-3 py-2 text-[13px] text-ink-700 hover:bg-ink-50"
                                >
                                  {STATUS_LABEL[s]}
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
              {claims.isLoading && (
                <tr><td colSpan={9} className="py-8 text-center text-ink-400">Loading…</td></tr>
              )}
              {!claims.isLoading && rows.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-ink-400">No claims found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      </> /* end claims tab */}
    </div>
  )
}
